import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logix2Dock } from "@/api/adminOneClient";
import Logix2VoiceControls from "@/components/Logix2VoiceControls";
import Logix2MicButton from "@/components/Logix2MicButton";
import Logix2ImageSearchButton from "@/components/Logix2ImageSearchButton";
import Logix2VoiceSettings from "@/components/Logix2VoiceSettings";
import OracleDataModeBadge from "@/components/oracle/OracleDataModeBadge";

/**
 * CareLogix™ ORACLE — LOGIX-2™ Bottom Intelligence Dock (Phase 16.6).
 *
 * Floating bottom-right agent dock visible on the public ORACLE home.
 *  - Free users see a locked preview + upgrade CTA.
 *  - Pro and above: chat + daily brief + watchlist add.
 *  - Executive and above: AdminOne escalation button.
 *  - Enterprise / AdminOne bearer: full feature set.
 *
 * Strictly additive — never replaces existing surfaces.
 * GPU-safe animations; respects prefers-reduced-motion.
 */
const MASCOT = "/brand/logix2-mascot-sq-192.png";        // square crop for FAB & avatars
const MASCOT_LARGE = "/brand/logix2-mascot-512.png";      // full-bleed for lock state
const MASCOT_HEADER = "/brand/logix2-mascot-sq-96.png";   // panel header
const HINT_KEY = "carelogix_logix2_dock_hint_seen";

const PROMPT_PRESETS = [
  "LOGIX-2, give me today's ORACLE brief.",
  "LOGIX-2, what stocks should I watch today?",
  "LOGIX-2, give me today's top crypto setups.",
  "LOGIX-2, give me today's Africa investment brief.",
  "LOGIX-2, show me Nigeria fintech opportunities.",
  "LOGIX-2, compare Kenya, Rwanda and Ghana for investment risk.",
  "LOGIX-2, what AI infrastructure signals should I watch?",
  "LOGIX-2, what AI sovereignty trends are forming?",
  "LOGIX-2, what should I avoid today?",
  "LOGIX-2, where should I set stop-loss?",
  "LOGIX-2, what sports lines have value?",
];

function MarkdownLite({ text }) {
  // Very small markdown shim — keeps the dock lightweight.
  if (!text) return null;
  const lines = text.split("\n");
  return (
    <div className="l2d-md">
      {lines.map((ln, i) => {
        if (ln.startsWith("## ")) return <h3 key={i} className="l2d-md__h">{ln.slice(3)}</h3>;
        if (ln.startsWith("# "))  return <h3 key={i} className="l2d-md__h">{ln.slice(2)}</h3>;
        if (/^\*.*\*$/.test(ln) && !ln.startsWith("**")) {
          return <p key={i} className="l2d-md__discl">{ln.slice(1, -1)}</p>;
        }
        if (ln.startsWith("- ")) return <div key={i} className="l2d-md__bullet">◆ {ln.slice(2).replace(/\*\*(.*?)\*\*/g, "$1")}</div>;
        if (!ln.trim()) return <div key={i} className="l2d-md__sp" />;
        const parts = ln.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} className="l2d-md__p">
            {parts.map((p, j) => (j % 2 === 1 ? <strong key={j}>{p}</strong> : p))}
          </p>
        );
      })}
    </div>
  );
}

export default function Logix2Dock() {
  const [open, setOpen] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);
  const [tab, setTab] = useState("chat");          // chat | brief | watchlist | voice | image | foodrx
  const [access, setAccess] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [watchSymbol, setWatchSymbol] = useState("");
  const [watchToast, setWatchToast] = useState("");
  const [sessionId, setSessionId] = useState(null);
  // Phase 20 — voice / image
  const [greeting, setGreeting] = useState(null);
  const [voiceSettings, setVoiceSettings] = useState({ enabled: true, auto_read: true, provider: "browser" });
  const [imageAnalysis, setImageAnalysis] = useState(null); // {analysis, image_data_url, prompt}
  // FoodRx Coach (Final Daily Habit Intelligence Pack)
  const [foodrxToday, setFoodrxToday] = useState(null);
  const [foodrxInput, setFoodrxInput] = useState("");
  const [foodrxThread, setFoodrxThread] = useState([]);
  const [foodrxBusy, setFoodrxBusy] = useState(false);
  const dockRef = useRef(null);

  // Resolve member access on mount + show "Ask LOGIX-2" hint once per browser.
  useEffect(() => {
    let mounted = true;
    logix2Dock.memberAccess()
      .then(({ data }) => { if (mounted) setAccess(data); })
      .catch(() => { if (mounted) setAccess({ tier: "free", features: { chat: false, preview: true, daily_brief: false, watchlist: false, escalate: false, label: "Preview only" } }); });

    // Phase 20 — load greeting + voice settings (best-effort, no UI blocking)
    logix2Dock.voiceGreeting()
      .then(({ data }) => { if (mounted) setGreeting(data); })
      .catch(() => { /* anonymous fallback */ });
    logix2Dock.voiceSettings()
      .then(({ data }) => { if (mounted && data?.settings) setVoiceSettings(data.settings); })
      .catch(() => { /* keep defaults */ });

    // First-visit hint — show after a brief delay, dismiss after 8s or on open.
    try {
      const seen = localStorage.getItem(HINT_KEY);
      if (!seen) {
        const t1 = setTimeout(() => { if (mounted) setHintVisible(true); }, 1600);
        const t2 = setTimeout(() => { if (mounted) setHintVisible(false); }, 10000);
        return () => { mounted = false; clearTimeout(t1); clearTimeout(t2); };
      }
    } catch { /* ignore */ }
    return () => { mounted = false; };
  }, []);

  const dismissHint = useCallback(() => {
    setHintVisible(false);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
  }, []);

  // LOGIX-2 Interaction Recovery — unified launcher.
  // Bulletproof: always opens the dock, dismisses any hint, and listens to
  // a custom window event so /logix2 route navigation (or any other entry
  // point) can request opening the dock from anywhere in the app.
  const openLogix2 = useCallback(() => {
    setOpen(true);
    setHintVisible(false);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
  }, []);

  // Listen for global "carelogix:open-logix2" events + ?logix2=open URL param.
  useEffect(() => {
    const onOpenEvent = () => openLogix2();
    window.addEventListener("carelogix:open-logix2", onOpenEvent);
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("logix2") === "open") openLogix2();
    } catch (_e) { /* ignore */ }
    return () => window.removeEventListener("carelogix:open-logix2", onOpenEvent);
  }, [openLogix2]);

  // Close dock when clicking outside (when expanded)
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (dockRef.current && !dockRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const tier = access?.tier || "free";
  const features = access?.features || { chat: false, preview: true, daily_brief: false, watchlist: false, escalate: false, admin_queue: false, label: "Preview only" };
  const isLocked = !features.chat;

  const loadBrief = useCallback(async () => {
    if (!features.daily_brief) return;
    setBriefLoading(true);
    try {
      const { data } = await logix2Dock.dailyBrief("all");
      setBrief(data);
    } catch {
      setBrief({ error: "Brief unavailable. Please retry shortly." });
    } finally {
      setBriefLoading(false);
    }
  }, [features.daily_brief]);

  useEffect(() => {
    if (open && tab === "brief" && !brief && !briefLoading && features.daily_brief) {
      loadBrief();
    }
  }, [open, tab, brief, briefLoading, features.daily_brief, loadBrief]);

  // FoodRx Coach loader — fetches today's plan summary when the tab is opened.
  useEffect(() => {
    if (!open || tab !== "foodrx" || foodrxToday) return undefined;
    let mounted = true;
    (async () => {
      try {
        const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
        const r = await fetch(`${base}/api/oracle/foodrx/coach/today`);
        const j = r.ok ? await r.json() : null;
        if (mounted) setFoodrxToday(j);
      } catch { if (mounted) setFoodrxToday({ error: "FoodRx Coach unavailable. Retry shortly." }); }
    })();
    return () => { mounted = false; };
  }, [open, tab, foodrxToday]);

  const sendFoodrx = useCallback(async (text) => {
    const q = (text || foodrxInput).trim();
    if (!q || foodrxBusy) return;
    setFoodrxInput("");
    const userMsg = { role: "user", content: q };
    setFoodrxThread((t) => [...t, userMsg]);
    setFoodrxBusy(true);
    try {
      const base = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
      const r = await fetch(`${base}/api/oracle/foodrx/coach/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: "logix2-dock", message: q }),
      });
      const j = r.ok ? await r.json() : { reply: "FoodRx Coach unavailable. Confirm with your clinician." };
      setFoodrxThread((t) => [...t, { role: "assistant", content: j.reply || "—" }]);
    } catch {
      setFoodrxThread((t) => [...t, { role: "assistant", content: "FoodRx Coach offline. No fabricated recommendations." }]);
    } finally {
      setFoodrxBusy(false);
    }
  }, [foodrxInput, foodrxBusy]);

  const sendChat = useCallback(async (text) => {
    if (!features.chat) return;
    const q = (text || input).trim();
    if (!q) return;
    // Voice interruption — stop any in-flight TTS before issuing a new request.
    try {
      if (typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel();
      }
    } catch { /* ignore */ }
    setInput("");
    const next = [...messages, { role: "user", content: q }];
    setMessages(next);
    setThinking(true);
    try {
      const userName = greeting?.short_name || undefined;
      const { data } = await logix2Dock.chat({ messages: next, session_id: sessionId || undefined, domain: "general", user_name: userName });
      if (data?.session_id) setSessionId(data.session_id);
      // Surface degraded-mode banner — now with provider-specific reason from /api/oracle/llm/health
      const isDegraded = data?.metadata?.upstream_status === "degraded";
      const errDetail  = data?.error?.detail || "";
      const errType    = data?.error?.type || "";
      const reply = data?.reply || "(No reply.)";
      let finalReply = reply;
      if (isDegraded && !reply.includes("offline intelligence mode active")) {
        const banner = errType === "UPSTREAM_LLM_FAILURE" && errDetail
          ? `## LLM provider unavailable\n${errDetail}\n`
          : "## LOGIX-2 reasoning temporarily degraded — LLM provider unavailable.";
        finalReply = `${banner}\n\n${reply}`;
      }
      setMessages([...next, { role: "assistant", content: finalReply, live_data: data?.live_data || null }]);
    } catch (e) {
      const status = e?.response?.status;
      const fallback =
        "## LLM provider unavailable\n" +
        "- Live LLM provider could not be reached.\n" +
        (status ? `- HTTP status: ${status}\n` : "") +
        "- Diagnostics: /api/oracle/llm/health\n\n" +
        "*This is AI research for educational purposes only — not financial advice.*";
      setMessages([...next, { role: "assistant", content: fallback }]);
    } finally {
      setThinking(false);
    }
  }, [features.chat, input, messages, sessionId, greeting]);

  const addWatch = useCallback(async () => {
    if (!features.watchlist) return;
    const symbol = watchSymbol.trim().toUpperCase();
    if (!symbol) { setWatchToast("Enter a symbol."); return; }
    try {
      await logix2Dock.watchlistAdd({ symbol });
      setWatchToast(`${symbol} added to LOGIX-2 watchlist ✓`);
      setWatchSymbol("");
      setTimeout(() => setWatchToast(""), 2500);
    } catch {
      setWatchToast("Failed to add — please retry.");
      setTimeout(() => setWatchToast(""), 2500);
    }
  }, [watchSymbol, features.watchlist]);

  const escalate = useCallback(async () => {
    if (!features.escalate) return;
    try {
      await logix2Dock.escalate({ summary: "Manual escalation from LOGIX-2 dock", severity: "HIGH" });
      setWatchToast("Compliance escalation queued ✓");
      setTimeout(() => setWatchToast(""), 2500);
    } catch {
      setWatchToast("Escalation failed — please retry.");
      setTimeout(() => setWatchToast(""), 2500);
    }
  }, [features.escalate]);

  const tierBadgeTone = useMemo(() => {
    if (tier === "enterprise") return "elite";
    if (tier === "executive")  return "exc";
    if (tier === "professional" || tier === "pro") return "pro";
    return "free";
  }, [tier]);

  return (
    <div className={`l2d ${open ? "l2d--open" : "l2d--closed"}`} ref={dockRef} data-testid="logix2-dock">
      {!open && (
        <>
          {hintVisible && (
            <button
              type="button"
              className="l2d-hint"
              onClick={openLogix2}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLogix2(); } }}
              data-testid="logix2-dock-hint"
              aria-label="Ask LOGIX-2"
              style={{ cursor: "pointer", touchAction: "manipulation", pointerEvents: "auto" }}
            >
              <span className="l2d-hint__arrow" aria-hidden="true" />
              <span className="l2d-hint__title">Meet LOGIX-2™</span>
              <span className="l2d-hint__sub">Your decision-support assistant for Markets · Crypto · FX · Sports · Macro. Click to ask.</span>
              <span className="l2d-hint__close" onClick={(e) => { e.stopPropagation(); dismissHint(); }} aria-label="Dismiss">✕</span>
            </button>
          )}
          <button
            type="button"
            className="l2d-fab"
            onClick={openLogix2}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openLogix2(); } }}
            aria-label="Open LOGIX-2 agent"
            data-testid="logix2-dock-fab"
            style={{ cursor: "pointer", touchAction: "manipulation", pointerEvents: "auto" }}
          >
            <span className="l2d-fab__ring" aria-hidden="true" />
            <span className="l2d-fab__pulse" aria-hidden="true" />
            <img src={MASCOT} alt="LOGIX-2 mascot" className="l2d-fab__img" />
            <span className="l2d-fab__chip">
              <span className="l2d-fab__chip-name">LOGIX-2™</span>
              <span className="l2d-fab__chip-tag">Ask the Agent</span>
            </span>
          </button>
        </>
      )}

      {open && (
        <div className="l2d-panel" role="dialog" aria-label="LOGIX-2 agent">
          <header className="l2d-panel__hd">
            <div className="l2d-panel__brand">
              <img src={MASCOT_HEADER} alt="" className="l2d-panel__mascot" aria-hidden="true" />
              <div>
                <div className="l2d-panel__title">LOGIX-2™</div>
                <div className="l2d-panel__sub">Autonomous Agentic Intelligence · GIOS™</div>
              </div>
            </div>
            <div className="l2d-panel__hd-meta">
              <span className={`l2d-tier-badge l2d-tier-badge--${tierBadgeTone}`} data-testid="logix2-dock-tier">{(features.label || tier).toString()}</span>
              <button type="button" className="l2d-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="logix2-dock-close">✕</button>
            </div>
          </header>

          {/* Tab strip */}
          <div className="l2d-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={tab === "chat"} className={`l2d-tab ${tab === "chat" ? "is-active" : ""}`} onClick={() => setTab("chat")} data-testid="logix2-dock-tab-chat">💬 Ask LOGIX-2</button>
            <button type="button" role="tab" aria-selected={tab === "brief"} className={`l2d-tab ${tab === "brief" ? "is-active" : ""}`} onClick={() => setTab("brief")} data-testid="logix2-dock-tab-brief">📋 Daily Brief</button>
            <button type="button" role="tab" aria-selected={tab === "watchlist"} className={`l2d-tab ${tab === "watchlist" ? "is-active" : ""}`} onClick={() => setTab("watchlist")} data-testid="logix2-dock-tab-watchlist">⭐ Watchlist</button>
            <button type="button" role="tab" aria-selected={tab === "voice"} className={`l2d-tab ${tab === "voice" ? "is-active" : ""}`} onClick={() => setTab("voice")} data-testid="logix2-dock-tab-voice">🎙️ Voice</button>
            <button type="button" role="tab" aria-selected={tab === "image"} className={`l2d-tab ${tab === "image" ? "is-active" : ""}`} onClick={() => setTab("image")} data-testid="logix2-dock-tab-image">📷 Image Search</button>
            <button type="button" role="tab" aria-selected={tab === "foodrx"} className={`l2d-tab ${tab === "foodrx" ? "is-active" : ""}`} onClick={() => setTab("foodrx")} data-testid="logix2-dock-tab-foodrx">🥗 FoodRx Coach</button>
          </div>

          <div className="l2d-body">
            {/* LOCK gate for free tier */}
            {isLocked && (
              <div className="l2d-lock" data-testid="logix2-dock-locked">
                <img src={MASCOT_LARGE} alt="" className="l2d-lock__mascot" aria-hidden="true" />
                <div className="l2d-lock__title">LOGIX-2™ is a paid-member agent</div>
                <p className="l2d-lock__sub">
                  Unlock the decision-support assistant for Markets · Crypto · FX · Options · Sports · Macro intelligence. Daily briefs, top opportunities, stop-loss zones, and human-reviewed escalations.
                </p>
                <ul className="l2d-lock__list">
                  <li><b>Pro</b> — Daily briefs + 10 LOGIX-2 chats/day + watchlist</li>
                  <li><b>Executive</b> — Unlimited chats + priority escalation</li>
                  <li><b>Enterprise</b> — Everything + API access</li>
                </ul>
                <div className="l2d-lock__cta">
                  <a className="l2d-btn l2d-btn--gold" href="#/membership" data-testid="logix2-dock-upgrade">⬆ Upgrade to Pro</a>
                  <a className="l2d-btn" href="#/membership" data-testid="logix2-dock-see-plans">See plans</a>
                </div>
                <div className="l2d-lock__disclaimer">
                  Research and educational use only · 21+ for sports intelligence where legal.
                </div>
              </div>
            )}

            {/* CHAT tab */}
            {!isLocked && tab === "chat" && (
              <div className="l2d-chat" data-testid="logix2-dock-chat">
                {/* Login-name personalized greeting */}
                {greeting?.salutation && messages.length === 0 && (
                  <div className="l2d-greeting" data-testid="logix2-dock-greeting">
                    <span aria-hidden="true">🤖</span>{" "}
                    <strong>{greeting.salutation}</strong>{" "}
                    Here is LOGIX-2 — ask anything across stocks, crypto, FX, sports, Africa, Global AI, healthcare, or upload an image to analyse.
                  </div>
                )}
                {messages.length === 0 && (
                  <div className="l2d-chat__intro">
                    <p className="l2d-chat__intro-h">Try one of these:</p>
                    <div className="l2d-presets">
                      {PROMPT_PRESETS.map((p) => (
                        <button key={p} type="button" className="l2d-preset" onClick={() => sendChat(p)} data-testid="logix2-dock-preset">{p}</button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="l2d-chat__scroll" data-testid="logix2-dock-scroll">
                  {messages.map((m, i) => (
                    <div key={i} className={`l2d-msg l2d-msg--${m.role}`} data-testid={`logix2-dock-msg-${m.role}`}>
                      {m.role === "assistant" && <img src={MASCOT_HEADER} alt="" className="l2d-msg__avatar" aria-hidden="true" />}
                      <div className="l2d-msg__bubble">
                        {m.role === "assistant" && m.live_data && (
                          <div style={{ marginBottom: 8 }}>
                            <OracleDataModeBadge envelope={m.live_data} testid={`logix2-dock-data-mode-${i}`} />
                          </div>
                        )}
                        {m.role === "assistant" ? <MarkdownLite text={m.content} /> : <p>{m.content}</p>}
                        {/* Phase 20 — Voice controls on every assistant response */}
                        {m.role === "assistant" && voiceSettings?.enabled !== false && (
                          <Logix2VoiceControls
                            text={m.content}
                            compact
                            autoRead={voiceSettings?.auto_read && i === messages.length - 1}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {thinking && (
                    <div className="l2d-msg l2d-msg--assistant l2d-thinking" data-testid="logix2-dock-thinking">
                      <img src={MASCOT_HEADER} alt="" className="l2d-msg__avatar" aria-hidden="true" />
                      <div className="l2d-msg__bubble"><span className="l2d-dot" /><span className="l2d-dot" /><span className="l2d-dot" /></div>
                    </div>
                  )}
                </div>

                <div className="l2d-input">
                  <input
                    type="text"
                    placeholder="Ask LOGIX-2 — type, speak, or upload an image…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                    disabled={thinking}
                    data-testid="logix2-dock-input"
                  />
                  {/* Phase 20 — Mic + Image buttons (mobile-safe, browser-native first) */}
                  <Logix2MicButton
                    onResult={(txt) => setInput((cur) => (cur ? `${cur} ${txt}` : txt))}
                    title="Speak to LOGIX-2"
                  />
                  <Logix2ImageSearchButton
                    onResult={(payload) => {
                      if (payload?.analysis) {
                        setImageAnalysis(payload);
                        setTab("image");
                      }
                    }}
                    prompt="Analyse this image for LOGIX-2 research. Be specific about visible tickers, levels, or sports lines."
                    title="Upload chart, screenshot, or news image"
                  />
                  <button type="button" className="l2d-btn l2d-btn--gold" onClick={() => sendChat()} disabled={thinking || !input.trim()} data-testid="logix2-dock-send">
                    {thinking ? "…" : "Send"}
                  </button>
                </div>
              </div>
            )}

            {/* DAILY BRIEF tab */}
            {!isLocked && tab === "brief" && (
              <div className="l2d-brief" data-testid="logix2-dock-brief">
                <div className="l2d-brief__row">
                  <button type="button" className="l2d-btn l2d-btn--ghost" onClick={loadBrief} disabled={briefLoading} data-testid="logix2-dock-brief-refresh">
                    {briefLoading ? "Refreshing…" : "↻ Refresh brief"}
                  </button>
                  {features.escalate && (
                    <button type="button" className="l2d-btn l2d-btn--escalate" onClick={escalate} data-testid="logix2-dock-escalate">🚨 Escalate for review</button>
                  )}
                </div>
                {!brief && !briefLoading && <p className="l2d-empty">No brief yet.</p>}
                {briefLoading && <p className="l2d-empty">Synthesizing today&apos;s brief…</p>}
                {brief?.error && <p className="l2d-empty l2d-empty--bad">{brief.error}</p>}
                {brief && !brief.error && (
                  <>
                    <ul className="l2d-headlines">
                      {(brief.headlines || []).map((h, i) => (
                        <li key={i} className={`l2d-headline l2d-headline--${h.tone}`}>{h.text}</li>
                      ))}
                    </ul>
                    <h4 className="l2d-h4">Top Opportunities</h4>
                    <table className="l2d-table">
                      <thead><tr><th>Symbol</th><th>Score</th><th>Grade</th><th>Risk</th></tr></thead>
                      <tbody>
                        {(brief.top_opportunities || []).map((o, i) => (
                          <tr key={i} data-testid={`logix2-dock-brief-row-${i}`}>
                            <td><b>{o.symbol}</b></td>
                            <td>{o.oracle_score}</td>
                            <td>{o.grade}</td>
                            <td>{o.risk_level}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="l2d-discl">{brief.disclaimer}</p>
                  </>
                )}
              </div>
            )}

            {/* WATCHLIST tab */}
            {!isLocked && tab === "watchlist" && (
              <div className="l2d-watch" data-testid="logix2-dock-watchlist">
                <p className="l2d-watch__intro">Add a symbol to your LOGIX-2 watchlist. The agent will track it in the daily brief.</p>
                <div className="l2d-watch__row">
                  <input
                    type="text"
                    placeholder="e.g. NVDA · BTC · EUR/USD"
                    value={watchSymbol}
                    onChange={(e) => setWatchSymbol(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addWatch(); }}
                    data-testid="logix2-dock-watch-input"
                  />
                  <button type="button" className="l2d-btn l2d-btn--gold" onClick={addWatch} data-testid="logix2-dock-watch-add">+ Add</button>
                </div>
                {watchToast && <div className="l2d-toast" data-testid="logix2-dock-toast">{watchToast}</div>}
                {features.escalate && (
                  <div className="l2d-watch__row" style={{ marginTop: 10 }}>
                    <button type="button" className="l2d-btn l2d-btn--escalate" onClick={escalate} data-testid="logix2-dock-watch-escalate">🚨 Escalate current view to AdminOne</button>
                  </div>
                )}
              </div>
            )}

            {/* VOICE tab — Phase 20 */}
            {!isLocked && tab === "voice" && (
              <div className="l2d-voice-tab" data-testid="logix2-dock-voice">
                <p className="l2d-watch__intro">
                  {greeting?.salutation ? `${greeting.salutation} ` : ""}
                  Configure how LOGIX-2 speaks aloud. Browser TTS works on every device; ElevenLabs is used automatically when an API key is configured.
                </p>
                <Logix2VoiceSettings onChange={(s) => setVoiceSettings(s)} />
                <div style={{ marginTop: 12 }}>
                  <p className="l2d-watch__intro" style={{ marginBottom: 6 }}>Try a test phrase:</p>
                  <Logix2VoiceControls
                    text={`${greeting?.salutation || "Hello."} LOGIX-2 voice intelligence is online. This is a short test of read-aloud, pause, stop, and replay. Research and education only — not financial advice.`}
                    compact={false}
                  />
                </div>
              </div>
            )}

            {/* IMAGE SEARCH tab — Phase 20 */}
            {!isLocked && tab === "image" && (
              <div className="l2d-image-tab" data-testid="logix2-dock-image">
                <p className="l2d-watch__intro">
                  Upload a chart, sports odds screenshot, news headline, or financial report. LOGIX-2 will analyse it and return structured research notes.
                </p>
                <div className="l2d-watch__row" style={{ gap: 8 }}>
                  <Logix2ImageSearchButton
                    onResult={(p) => setImageAnalysis(p)}
                    prompt="Analyse this image for LOGIX-2 research. Be specific about visible tickers, levels, dates, or sports lines."
                    title="Choose image"
                  />
                  <span style={{ fontSize: 12, color: "var(--clx-muted)", alignSelf: "center" }}>Max 8 MB · PNG / JPG / WEBP / HEIC</span>
                </div>
                {imageAnalysis?.image_data_url && (
                  <div style={{ marginTop: 12 }}>
                    <img
                      src={imageAnalysis.image_data_url}
                      alt="Uploaded preview"
                      style={{ maxWidth: "100%", borderRadius: 10, border: "1px solid var(--clx-border)" }}
                      data-testid="logix2-dock-image-preview"
                    />
                  </div>
                )}
                {imageAnalysis?.analysis && (
                  <div className="l2d-image-result" data-testid="logix2-dock-image-result" style={{ marginTop: 12 }}>
                    {imageAnalysis.analysis.visible && (
                      <p><strong>Visible:</strong> {imageAnalysis.analysis.visible}</p>
                    )}
                    {imageAnalysis.analysis.extracted_context && (
                      <p><strong>Context:</strong> {imageAnalysis.analysis.extracted_context}</p>
                    )}
                    {Array.isArray(imageAnalysis.analysis.key_data_points) && imageAnalysis.analysis.key_data_points.length > 0 && (
                      <div>
                        <strong>Key data points:</strong>
                        <ul>{imageAnalysis.analysis.key_data_points.map((k, i) => <li key={i}>{k}</li>)}</ul>
                      </div>
                    )}
                    {Array.isArray(imageAnalysis.analysis.risks) && imageAnalysis.analysis.risks.length > 0 && (
                      <div>
                        <strong>⚠ Risks:</strong>
                        <ul>{imageAnalysis.analysis.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                      </div>
                    )}
                    {Array.isArray(imageAnalysis.analysis.follow_up) && imageAnalysis.analysis.follow_up.length > 0 && (
                      <div>
                        <strong>🔎 Follow-up:</strong>
                        <ul>{imageAnalysis.analysis.follow_up.map((f, i) => <li key={i}>{f}</li>)}</ul>
                      </div>
                    )}
                    <p style={{ fontSize: 11, color: "var(--clx-muted)", marginTop: 8 }}>
                      Confidence: {imageAnalysis.analysis.confidence ?? "—"}/10 · Human review: {imageAnalysis.analysis.human_review ? "REQUIRED" : "not required"}
                    </p>
                    {/* Read Aloud the visible/context summary */}
                    {voiceSettings?.enabled !== false && (
                      <Logix2VoiceControls
                        text={[
                          imageAnalysis.analysis.visible,
                          imageAnalysis.analysis.extracted_context,
                          (imageAnalysis.analysis.risks || []).join(". "),
                        ].filter(Boolean).join(". ")}
                        compact
                      />
                    )}
                    <p className="l2d-discl" style={{ marginTop: 8 }}>{imageAnalysis.analysis.disclaimer}</p>
                  </div>
                )}
              </div>
            )}

            {/* FOODRX COACH tab — Final Daily Habit Intelligence Pack */}
            {!isLocked && tab === "foodrx" && (
              <div className="l2d-foodrx-tab" data-testid="logix2-dock-foodrx" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <p className="l2d-watch__intro">
                  FoodRx Coach™ — your executive nutrition, hydration, and medication-food intelligence agent.
                  Educational only. <strong>No diagnosis. No autonomous ordering. No prescribing.</strong>
                </p>

                {foodrxToday && !foodrxToday.error && (
                  <div className="l2d-foodrx-status" data-testid="logix2-dock-foodrx-status" style={{ padding: "8px 12px", border: "1px solid rgba(245,200,66,0.30)", borderRadius: 10, background: "rgba(245,200,66,0.06)", color: "#fde68a", fontSize: 12 }}>
                    Agent: <strong>{foodrxToday.agent || "FoodRxCoach"}</strong> · Today&apos;s plan: <strong>{foodrxToday.reads_today_meal_plan ? "Loaded" : "—"}</strong>
                  </div>
                )}
                {foodrxToday && foodrxToday.error && (
                  <p className="l2d-empty l2d-empty--bad" data-testid="logix2-dock-foodrx-error">{foodrxToday.error}</p>
                )}

                {Array.isArray(foodrxToday && foodrxToday.quick_actions) && foodrxToday.quick_actions.length > 0 && (
                  <div className="l2d-presets" data-testid="logix2-dock-foodrx-quickactions">
                    {foodrxToday.quick_actions.map((q) => (
                      <button
                        key={q}
                        type="button"
                        className="l2d-preset"
                        onClick={() => sendFoodrx(`FoodRx Coach: ${q.replace(/_/g, ' ')}`)}
                        data-testid={`logix2-dock-foodrx-quick-${q}`}
                      >
                        {q.replace(/_/g, " ")}
                      </button>
                    ))}
                  </div>
                )}

                <div className="l2d-chat__scroll" data-testid="logix2-dock-foodrx-scroll" style={{ maxHeight: 240, overflowY: "auto" }}>
                  {foodrxThread.length === 0 && (
                    <p className="l2d-empty" style={{ fontSize: 12 }}>
                      Ask FoodRx Coach about your meal plan, hydration, medication-food interactions, flare risk, or recovery.
                    </p>
                  )}
                  {foodrxThread.map((m, i) => (
                    <div key={i} className={`l2d-msg l2d-msg--${m.role}`} data-testid={`logix2-dock-foodrx-msg-${m.role}`}>
                      {m.role === "assistant" && <img src={MASCOT_HEADER} alt="" className="l2d-msg__avatar" aria-hidden="true" />}
                      <div className="l2d-msg__bubble"><p>{m.content}</p></div>
                    </div>
                  ))}
                  {foodrxBusy && (
                    <div className="l2d-msg l2d-msg--assistant l2d-thinking" data-testid="logix2-dock-foodrx-thinking">
                      <img src={MASCOT_HEADER} alt="" className="l2d-msg__avatar" aria-hidden="true" />
                      <div className="l2d-msg__bubble"><span className="l2d-dot" /><span className="l2d-dot" /><span className="l2d-dot" /></div>
                    </div>
                  )}
                </div>

                <div className="l2d-input">
                  <input
                    type="text"
                    placeholder="Ask FoodRx Coach… meal plan · hydration · interactions · flare check"
                    value={foodrxInput}
                    onChange={(e) => setFoodrxInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendFoodrx(); } }}
                    disabled={foodrxBusy}
                    data-testid="logix2-dock-foodrx-input"
                  />
                  <button type="button" className="l2d-btn l2d-btn--gold" onClick={() => sendFoodrx()} disabled={foodrxBusy || !foodrxInput.trim()} data-testid="logix2-dock-foodrx-send">
                    {foodrxBusy ? "…" : "Send"}
                  </button>
                </div>

                <p className="l2d-discl" style={{ marginTop: 4 }}>
                  Educational coaching only. Confirm any medication-food interactions with your clinician or pharmacist. No diagnosis. No autonomous purchases.
                </p>
              </div>
            )}
          </div>

          <footer className="l2d-foot">
            <span>Powered by LOGIX-2™ · CareLogix™ ORACLE · GIOS™</span>
            <span className="l2d-foot__tier">{tier.toUpperCase()}</span>
          </footer>
        </div>
      )}
    </div>
  );
}
